import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, List, ListItemButton, Divider, TextField,
    InputAdornment, Tabs, Tab, Button, IconButton, CircularProgress,
    ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import gmailService from '../services/gmail-service';
import { useNavigate, useLocation } from 'react-router-dom';

const GmailMessagesPage = () => {
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [searchQuery, setSearchQuery] = useState('');
    const [tabValue, setTabValue] = useState(0);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sender, setSender] = useState('');
    const [senderSuggestions, setSenderSuggestions] = useState([]);
    const [hasAttachment, setHasAttachment] = useState(false);

    const [viewMode, setViewMode] = useState('html');

    const requestIdRef = useRef(0);
    const navigate = useNavigate();
    const location = useLocation();
    const [relatedOpen, setRelatedOpen] = useState(false);

    const getValidDate = (email) => {
        if (!email) return null;
        if (email.receiveddate) {
            const parsed = new Date(email.receiveddate);
            if (!isNaN(parsed)) return parsed;
        }
        if (email.internaldate) {
            const n = parseInt(email.internaldate, 10);
            if (!isNaN(n)) return new Date(n);
        }
        return null;
    };

    const formatTime = (email) => {
        const date = getValidDate(email);
        if (!date) return <span style={{ color: '#ff6b6b' }}>(No Date)</span>;
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
    };

    const formatFullDate = (email) => {
        const date = getValidDate(email);
        if (!date) return <span style={{ color: '#ff6b6b' }}>(Unknown Date)</span>;
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const getIframeContent = (email) => {
        if (!email) return '<html><body><p>No content</p></body></html>';
        let content = viewMode === 'html' ? (email.bodyhtml || email.bodyplaintext || '<p>No content available.</p>') : (email.bodyplaintext || email.bodyhtml || '<p>No content available.</p>');
        if (viewMode === 'html' && !content.toLowerCase().includes('<html')) {
            content = `
                <html>
                    <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 20px; color: #333; line-height: 1.6;">
                        ${content.replace(/\n/g, '<br/>')}
                    </body>
                </html>`;
        } else if (viewMode === 'text') {
            content = `<html><body style="font-family: monospace; padding: 20px; white-space: pre-wrap;">${content}</body></html>`;
        }
        return content;
    };

    const loadEmails = async (keyword = '', pageNum = 1) => {
        const localRequestId = ++requestIdRef.current;
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);
        try {
            const response = await gmailService.getMessages(pageNum, 50, keyword, dateFrom, dateTo, sender, hasAttachment ? 1 : null);
            if (localRequestId !== requestIdRef.current) return; // stale response
            if (response && response.success && response.result) {
                const fetchedEmails = response.result.messages || [];
                if (pageNum === 1) {
                    setEmails(fetchedEmails);
                    setSelectedEmail(fetchedEmails.length > 0 ? fetchedEmails[0] : null);
                    // derive sender suggestions from first page
                    const senders = Array.from(new Set(fetchedEmails.map(e => (e.fromemailaddress || '').toLowerCase()).filter(x => x)));
                    setSenderSuggestions(senders.slice(0, 50));
                } else {
                    setEmails(prev => [...prev, ...fetchedEmails]);
                }
                setHasMore(fetchedEmails.length >= 50);
            }
        } catch (error) {
            console.error('Error fetching emails:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const escapeHtml = (unsafe) => {
        if (!unsafe) return '';
        return unsafe.replace(/[&<>"']/g, function (m) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[m];
        });
    };

    const highlight = (text, term) => {
        if (!term) return escapeHtml(text);
        try {
            const t = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(`(${t})`, 'ig');
            return escapeHtml(text).replace(re, '<mark style="background: #ffd54f; color: #000; padding:0 2px;">$1</mark>');
        } catch (e) {
            return escapeHtml(text);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const kw = params.get('keyword') || '';
        const from = params.get('dateFrom') || '';
        const to = params.get('dateTo') || '';
        setSearchQuery(kw);
        setDateFrom(from);
        setDateTo(to);
        setPage(1);
        loadEmails(kw, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPage(1);
        loadEmails(searchQuery, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo]);

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const params = new URLSearchParams(location.search);
            if (searchQuery) params.set('keyword', searchQuery);
            else params.delete('keyword');
            params.set('page', '1');
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            setPage(1);
            loadEmails(searchQuery, 1);
        }
    };

    const handleRefresh = () => {
        const params = new URLSearchParams(location.search);
        if (searchQuery) params.set('keyword', searchQuery);
        if (dateFrom) params.set('dateFrom', dateFrom);
        if (dateTo) params.set('dateTo', dateTo);
        params.set('page', '1');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        setPage(1);
        loadEmails(searchQuery, 1);
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 5) {
            if (hasMore && !loadingMore && !loading) {
                const nextPage = page + 1;
                setPage(nextPage);
                loadEmails(searchQuery, nextPage);
            }
        }
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', backgroundColor: '#121212', color: '#ffffff' }}>
            
            {/* LEFT COLUMN: EMAIL LIST */}
            <Box sx={{ width: 350, flexShrink: 0, borderRight: '1px solid #333333', backgroundColor: '#1e1e1e', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth size="small" placeholder="Search & hit Enter..." variant="outlined"
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={handleSearch}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaaaaa' }} /></InputAdornment>,
                            sx: { color: '#ffffff', backgroundColor: '#2c2c2c', '& fieldset': { borderColor: '#444444' }, '&:hover fieldset': { borderColor: '#666666' }, '&.Mui-focused fieldset': { borderColor: '#90caf9' } }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField
                            size="small" placeholder="Sender (autocomplete)" variant="outlined"
                            value={sender} onChange={(e) => setSender(e.target.value)} list="senders-list"
                            sx={{ flex: 1, color: '#ffffff', backgroundColor: '#2c2c2c', '& fieldset': { borderColor: '#444444' } }}
                        />
                        <datalist id="senders-list">
                            {senderSuggestions.map((s, i) => <option key={i} value={s} />)}
                        </datalist>
                        <FormControlLabel
                            control={<Checkbox checked={hasAttachment} onChange={() => setHasAttachment(v => !v)} sx={{ color: '#90caf9' }} />}
                            label={<Typography sx={{ color: '#aaaaaa', fontSize: '0.85rem' }}>Has attachment</Typography>}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField
                            type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
                            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: '#ffffff' }, '& .MuiInputLabel-root': { color: '#aaaaaa' }, backgroundColor: '#2c2c2c', '& fieldset': { borderColor: '#444444' } }}
                        />
                        <TextField
                            type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
                            value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: '#ffffff' }, '& .MuiInputLabel-root': { color: '#aaaaaa' }, backgroundColor: '#2c2c2c', '& fieldset': { borderColor: '#444444' } }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #333333', px: 1 }}>
                    <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} sx={{ minHeight: 40, '& .MuiTab-root': { color: '#aaaaaa', minHeight: 40, textTransform: 'none', minWidth: 'auto', px: 1.5 }, '& .Mui-selected': { color: '#90caf9' }, '& .MuiTabs-indicator': { backgroundColor: '#90caf9' } }}>
                        <Tab label="Inbox" />
                    </Tabs>
                    <IconButton size="small" sx={{ color: '#aaaaaa', ml: 'auto' }} onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }} onScroll={handleScroll}>
                    {loading && page === 1 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#90caf9' }} /></Box>
                    ) : emails.length > 0 ? (
                        <>
                            {(
                                // apply client-side filters for sender and hasAttachment (approximate)
                                emails.filter(email => {
                                    if (sender) {
                                        const fe = (email.fromemailaddress || '').toLowerCase();
                                        if (!fe.includes(sender.toLowerCase())) return false;
                                    }
                                    if (hasAttachment) {
                                        const hay = ((email.snippet || '') + ' ' + (email.subject || '')).toLowerCase();
                                        if (!/attach|attached|attachment|pdf|invoice|receipt/.test(hay)) return false;
                                    }
                                    return true;
                                }).map((email, idx) => {
                                const isSelected = selectedEmail?.zoegmailmessageid === email.zoegmailmessageid;
                                return (
                                    <React.Fragment key={`${email.zoegmailmessageid}-${idx}`}>
                                        <ListItemButton 
                                            selected={isSelected} onClick={() => setSelectedEmail(email)}
                                            sx={{ 
                                                flexDirection: 'column', alignItems: 'flex-start', py: 1.5, px: 2,
                                                backgroundColor: isSelected ? '#2a3b4c' : 'transparent', 
                                                '&:hover': { backgroundColor: isSelected ? '#2a3b4c' : '#2c2c2c' },
                                                '&.Mui-selected': { backgroundColor: '#2a3b4c' },
                                                '&.Mui-selected:hover': { backgroundColor: '#2a3b4c' }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? '#ffffff' : '#dddddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                                                    {email.fromemailaddress?.split('@')[0] || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#aaaaaa' }}>
                                                    {formatTime(email)}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }} dangerouslySetInnerHTML={{ __html: highlight(email.subject || '(No Subject)', searchQuery) }} />
                                            <Typography variant="caption" sx={{ color: '#888888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }} dangerouslySetInnerHTML={{ __html: highlight(email.snippet || '(No preview available)', searchQuery) }} />
                                        </ListItemButton>
                                        <Divider sx={{ borderColor: '#333333' }} />
                                    </React.Fragment>
                                );
                            })
                            )}
                            
                            {loadingMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} sx={{ color: '#90caf9' }} /></Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4, color: '#666666' }}><Typography>No emails found.</Typography></Box>
                    )}
                </List>
            </Box>

            {/* MIDDLE COLUMN: EMAIL VIEWER */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#121212' }}>
                {loading && page === 1 ? (
                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}><CircularProgress sx={{ color: '#90caf9' }} /></Box>
                ) : selectedEmail ? (
                    <>
                        <Box sx={{ p: 3, backgroundColor: '#1e1e1e', borderBottom: '1px solid #333333' }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#ffffff' }}>
                                {selectedEmail.subject || '(No Subject)'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#dddddd' }}>
                                    <strong style={{ color: '#aaaaaa' }}>From:</strong> {selectedEmail.fromemailaddress}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#aaaaaa' }}>
                                    {formatFullDate(selectedEmail)}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#dddddd' }}>
                                <strong style={{ color: '#aaaaaa' }}>To:</strong> {selectedEmail.toemailaddress}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                    size="small"
                                >
                                    <ToggleButton value="html" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#90caf9', color: '#000' } }}>HTML</ToggleButton>
                                    <ToggleButton value="text" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#90caf9', color: '#000' } }}>Text</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>

                        <Box sx={{ flexGrow: 1, p: 0, backgroundColor: '#ffffff' }}>
                            <iframe 
                                title="Email Content"
                                srcDoc={getIframeContent(selectedEmail)}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                sandbox="allow-same-origin"
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#666666' }}>Select an email to read</Typography>
                    </Box>
                )}
            </Box>

            {/* RIGHT COLUMN: RELATED CONTENT (collapsible) */}
            <Box sx={{
                width: relatedOpen ? 250 : 36,
                flexShrink: 0,
                borderLeft: '1px solid #333333',
                backgroundColor: '#1e1e1e',
                p: relatedOpen ? 2 : 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                transition: 'width 200ms ease'
            }}>
                {!relatedOpen ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => setRelatedOpen(true)} sx={{ color: '#90caf9' }}>
                            <ChevronLeftIcon sx={{ transform: 'rotate(180deg)' }} />
                        </IconButton>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, textTransform: 'uppercase', color: '#aaaaaa', fontSize: '0.75rem' }}>Related Content</Typography>
                            <IconButton size="small" onClick={() => setRelatedOpen(false)} sx={{ color: '#aaaaaa' }}>
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#dddddd' }}>Trip</Typography>
                            <Button startIcon={<AddCircleOutlineIcon />} sx={{ textTransform: 'none', color: '#90caf9', justifyContent: 'flex-start', width: '100%' }}>Add trip</Button>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#dddddd' }}>Statement</Typography>
                            <Button startIcon={<AddCircleOutlineIcon />} sx={{ textTransform: 'none', color: '#90caf9', justifyContent: 'flex-start', width: '100%' }}>Add statement</Button>
                        </Box>
                    </>
                )}
            </Box>

        </Box>
    );
};

export default GmailMessagesPage;